import { createRouter, createWebHistory, type Router } from 'vue-router'
import routes from '@/router/routes'
import type { INavigationService } from '@/interfaces'
import {
  LOGIN_URL,
  HOME_URL,
  BASE_URL,
  RESET_PASSWORD_URL,
  SET_PASSWORD_URL
} from '@/utils/constants'
import CookieStorageService from '@/services/CookieStorageService'
import { logError, logInfo } from '@/utils/helpers/logger'
import { checkAppVersion } from '@/utils/helpers/app-helpers'

class NavigationService implements INavigationService {
  router: Router

  constructor() {
    this.router = createRouter({
      history: createWebHistory(BASE_URL),
      routes
    })
    this.setupNavigationConfigurations()
  }

  public getRouterInstance() {
    return this.router
  }

  public canGoBack(): boolean {
    return !!this.router.options.history.state.back
  }

  public navigateTo(route: string, query?: any): void {
    this.router.push({ path: route, query }).catch((error) => logError('Redirect Error: ', error))
  }

  public replaceCurrentPath(route: string, query?: any) {
    this.router.replace({ path: route, query }).catch((error) => logError('Replace Current Path Error: ', error))
  }

  public goBack(): void {
    this.router.back()
  }

  public getCurrentRoute() {
    return this.router.currentRoute.value
  }

  public getCurrentRouteParams(): any {
    return this.router.currentRoute.value.params
  }

  public setupNavigationConfigurations() {
    this.setupNavigationGuards()
    this.setupAutoReload()
  }

  public setupNavigationGuards() {
    this.router.beforeEach((to, from, next) => {
      // const isReadPermission = PermissionsService.checkReadPermission(to?.name as string);
      const hasValidTokens = NavigationService.checkIfTokensArePresent()
      try {
        if (to.path === RESET_PASSWORD_URL || to.path === SET_PASSWORD_URL) {
          logInfo('RESETTING PASSWORD')
          next()
        } else if (!hasValidTokens && to.path !== LOGIN_URL) {
          logInfo('GOING TO LOGIN')
          next(LOGIN_URL)
        } else if (hasValidTokens && to.path === LOGIN_URL) {
          logInfo('GOING HOME')
          next(HOME_URL)
        }
        // else if (tokensHaveNotExpired && to.path !== LOGIN_URL && !isReadPermission) {
        //   logInfo('GOING TO HOME')
        //   next(HOME_URL)
        // }
        else {
          logInfo('GOING TO NEXT', to.path)
          next()
        }
      } catch (error: any) {
        logError(error)
      }
      return false
    })
    this.router.onError((error) => logError('Navigation Error', error))
  }

  protected setupAutoReload() {
    this.router.afterEach(() => {
      checkAppVersion()
    })
  }

  static checkIfTokensArePresent(): boolean {
    const cookies = new CookieStorageService()
    const isUserAuthenticated = Boolean(cookies.getData('userId') && cookies.getData('accessToken'))
    try {
      if (!isUserAuthenticated) {
        throw new Error('User not logged in!')
      }
    } catch (error: any) {
      logInfo(error)
    }
    return isUserAuthenticated
  }
}

export default NavigationService
